import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function CustomSelect({
  value,
  onChange,
  options = [],
  placeholder = "Pilih...",
  className = "",
  required = false,
  disabled = false,
  error = false,
  searchable = false,
}) {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [query, setQuery] = useState("");
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));

  const visibleOptions = searchable && query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  // Position the dropdown fixed relative to the trigger button
  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownH = Math.min(options.length * 42, 224) + (searchable ? 42 : 0); // max-h-56 = 224px
    const openAbove = spaceBelow < dropdownH + 8 && rect.top > dropdownH;

    setDropdownStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  };

  useEffect(() => {
    if (!open) return;
    setQuery("");
    updatePosition();

    const handleScroll = () => updatePosition();
    const handleResize = () => updatePosition();
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    const handleMouseDown = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);

    // Portal content mounts after this effect runs, so focus needs to wait a tick.
    const focusTimer = searchable ? window.setTimeout(() => searchRef.current?.focus(), 0) : null;

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleMouseDown);
      if (focusTimer) window.clearTimeout(focusTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = (val) => {
    onChange(val);
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      {required && (
        <input
          tabIndex={-1}
          required
          value={value ?? ""}
          onChange={() => {}}
          className="absolute inset-0 opacity-0 pointer-events-none"
          aria-hidden="true"
        />
      )}

      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen((v) => !v); }}
        className={`glass-input w-full flex items-center justify-between gap-2 text-left text-sm ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        // glass-input sets its own border color via CSS custom properties, so
        // an inline override is the reliable way to force red — appending a
        // Tailwind utility class isn't guaranteed to win the cascade over it.
        style={error ? { borderColor: "var(--danger)" } : undefined}
      >
        <span className={selected ? "text-strong" : "text-muted"}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}
          style={{ color: "var(--text-secondary)" }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && createPortal(
        <div
          ref={dropdownRef}
          className="glass-dropdown rounded-xl overflow-hidden flex flex-col"
          style={dropdownStyle}
        >
          {searchable && (
            <div className="p-1.5 flex-shrink-0" style={{ borderBottom: "1px solid var(--border)" }}>
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Cari..."
                className="glass-input w-full text-sm"
              />
            </div>
          )}
          <div className="max-h-56 overflow-y-auto">
            {visibleOptions.length === 0 && (
              <div className="px-3 py-2.5 text-sm text-muted">Tidak ditemukan.</div>
            )}
            {visibleOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(opt.value)}
                className={`w-full text-left px-3 py-2.5 text-sm transition-colors hover:bg-white/40 dark:hover:bg-white/5 ${
                  String(opt.value) === String(value) ? "font-semibold" : ""
                }`}
                style={{ color: String(opt.value) === String(value) ? "var(--accent)" : "var(--text-primary)" }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
